import React from "react";
import Icon from "../../../components/AppIcon";
import { useNavigate } from "react-router-dom";

const PROPS = [
  {
    icon: "Users",
    color: "from-emerald-600 to-emerald-500",
    accent: "bg-emerald-50 text-emerald-600 border-emerald-200",
    tag: "Expert Guidance",
    title: "Guided by Generations of Sherpa Wisdom",
    description:
      "Our guides carry centuries of mountain knowledge passed down through generations. Every expedition is led by certified professionals who know every trail, weather pattern, and sacred site.",
    stats: [
      { value: "15+", label: "Avg. Years Experience" },
      { value: "5+", label: "Certified Guides" },
      { value: "98%", label: "Success Rate" },
    ],
    features: [
      "International Mountain Guide Certification",
      "Wilderness First Aid Training",
      "Cultural Heritage Expertise",
      "Multi-language Communication",
    ],
    image: "/assets/images/summit.jpeg",
  },
  {
    icon: "Heart",
    color: "from-amber-500 to-accent",
    accent: "bg-amber-50 text-amber-600 border-amber-200",
    tag: "Cultural Immersion",
    title: "Transformative Cultural Experiences",
    description:
      "Beyond breathtaking peaks, discover the soul of the Himalayas. Participate in monastery ceremonies, learn traditional crafts, and share meals with local families.",
    stats: [
      { value: "100+", label: "Cultural Sites" },
      { value: "10+", label: "Local Partnerships" },
      { value: "12+", label: "Festivals Covered" },
    ],
    features: [
      "Monastery Visits & Ceremonies",
      "Traditional Craft Workshops",
      "Local Family Homestays",
      "Festival Participation",
    ],
    image: "/assets/images/Ancient.jpg",
  },
  {
    icon: "Shield",
    color: "from-sky-600 to-sky-500",
    accent: "bg-sky-50 text-sky-600 border-sky-200",
    tag: "Safety First",
    title: "Unmatched Safety & Expertise",
    description:
      "Your safety is our highest priority. We maintain the industry's highest safety standards with comprehensive risk management, emergency protocols, and state-of-the-art equipment.",
    stats: [
      { value: "99.9%", label: "Safety Record" },
      { value: "30 min", label: "Emergency Response" },
      { value: "Pro", label: "Equipment Grade" },
    ],
    features: [
      "24/7 Emergency Support",
      "Satellite Communication",
      "Medical Emergency Training",
      "Weather Monitoring Systems",
    ],
    image: "/assets/images/safety.jpg",
  },
];

const ValuePropositions = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">
            <Icon name="Star" size={14} />
            Why Choose Us
          </span>
          <h2 className="section-heading text-foreground mb-5">
            Why Choose{" "}
            <span style={{ color: "#2d5016" }}>Sherpa Trekker</span>?
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            We don't just guide you to the summit — we transform your journey into a life-changing experience.
          </p>
        </div>

        {/* Propositions */}
        <div className="space-y-16 lg:space-y-24">
          {PROPS.map((prop, i) => (
            <div
              key={i}
              className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              {/* Image */}
              <div className="relative group">
                <div className={`absolute -inset-4 bg-gradient-to-br ${prop.color} opacity-10 rounded-3xl blur-xl`} />
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src={prop.image}
                    alt={prop.title}
                    loading="lazy"
                    className="w-full h-72 lg:h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2.5 shadow-lg">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${prop.color} flex items-center justify-center`}>
                        <Icon name={prop.icon} size={16} color="white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 leading-none">{prop.tag}</p>
                        <p className="text-sm font-bold text-gray-900 mt-0.5">{prop.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${prop.accent}`}>
                  <Icon name={prop.icon} size={12} />
                  {prop.tag}
                </span>

                <h3 className="font-headline text-2xl sm:text-3xl text-gray-900 mb-4" style={{ fontWeight: 300 }}>
                  {prop.title}
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  {prop.description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-8">
                  {prop.stats.map((s, si) => (
                    <div key={si} className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                      <div className="font-headline text-2xl font-bold text-primary mb-0.5">{s.value}</div>
                      <div className="text-xs text-text-secondary">{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Features */}
                <ul className="space-y-2.5 mb-8">
                  {prop.features.map((f, fi) => (
                    <li key={fi} className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={11} className="text-primary" />
                      </span>
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate("/trek-discovery-hub")}
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-accent text-accent font-semibold rounded-xl hover:bg-accent hover:text-white transition-all duration-200 text-sm"
                >
                  Explore Our Treks
                  <Icon name="ArrowRight" size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 lg:p-12 text-center max-w-4xl mx-auto shadow-lg">
          <div className="w-14 h-14 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-md">
            <Icon name="Compass" size={28} color="white" />
          </div>
          <h3 className="font-headline text-2xl sm:text-3xl text-white mb-3" style={{ fontWeight: 300 }}>
            Ready to Begin Your{" "}
            <span style={{ color: "#c4873a" }}>Journey</span>?
          </h3>
          <p className="text-white/70 mb-8 max-w-lg mx-auto">
            Let our experts help you plan the perfect Himalayan adventure tailored to your dreams and abilities.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/booking-consultation-center")}
              className="flex items-center justify-center gap-2 px-7 py-3.5 bg-accent text-white font-semibold rounded-xl hover:bg-accent/90 transition-colors shadow-sm"
            >
              <Icon name="Calendar" size={16} />
              Schedule Consultation
            </button>
            <a
              href="tel:+9779851157213"
              className="flex items-center justify-center gap-2 px-7 py-3.5 border-2 border-white/20 text-white font-semibold rounded-xl hover:border-white/40 transition-colors"
            >
              <Icon name="Phone" size={16} />
              Speak with Expert
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuePropositions;
